import { ArrowLeft, Building2 } from "lucide-react";
import { useState } from "react";

import { AppealModal } from "@/components/appeal-modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { InfoBox } from "@/components/ui/info-box";
import { SearchInput } from "@/components/ui/search-input";
import { SectionHeader } from "@/components/ui/section-header";
import type { BlockedDomain, Company } from "@/lib/types";

interface CompanyDetailProps {
  company: Company | null;
  onBack: () => void;
}

export function CompanyDetail({ company, onBack }: CompanyDetailProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<BlockedDomain | null>(
    null
  );

  if (!company) {
    return (
      <EmptyState
        title="No results found"
        message="The company you searched for could not be found."
        actionLabel="Back to Search"
        onAction={onBack}
      />
    );
  }

  const filteredDomains = company.blockedDomains.filter(
    (item) =>
      item.domain.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.caseNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.reason.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAppeal = (domain: BlockedDomain) => {
    setSelectedDomain({ ...domain, companyName: company.name });
    setShowAppealModal(true);
  };

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        onClick={onBack}
        className="flex items-center gap-2"
      >
        <ArrowLeft size={20} />
        Back to Search
      </Button>

      <Card>
        <CardHeader
          icon={<Building2 size={24} />}
          title={company.name}
          subtitle={`${company.blockedDomains.length} domain${company.blockedDomains.length !== 1 ? "s" : ""} blocked`}
        />

        <CardContent className="space-y-6">
          <div>
            <SectionHeader>Company Policy</SectionHeader>
            <p className="text-gray-700 text-sm sm:text-base">
              {company.policy}
            </p>
          </div>

          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-4">
              <SectionHeader className="mb-0">Blocked Domains</SectionHeader>
              <span className="text-sm text-gray-600">
                {company.blockedDomains.length} total domain
                {company.blockedDomains.length !== 1 ? "s" : ""} blocked
              </span>
            </div>

            <div className="mb-4">
              <SearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search blocked domains..."
              />
              {searchQuery && (
                <p className="text-sm text-gray-600 mt-2">
                  Showing {filteredDomains.length} of{" "}
                  {company.blockedDomains.length} domain
                  {filteredDomains.length !== 1 ? "s" : ""}
                </p>
              )}
            </div>

            <div className="space-y-4">
              {filteredDomains.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm sm:text-base">
                  No domains match your search.
                </div>
              ) : (
                filteredDomains.map((item, index) => (
                  <BlockedDomainCard
                    key={index}
                    domain={item}
                    onAppeal={handleAppeal}
                  />
                ))
              )}
            </div>
          </div>

          <InfoBox variant="gray">
            <SectionHeader className="mb-2">
              About {company.name}&apos;s Enforcement
            </SectionHeader>
            <p className="text-sm text-gray-700">{company.enforcement}</p>
          </InfoBox>
        </CardContent>
      </Card>

      <AppealModal
        notice={selectedDomain}
        searchType="domain"
        isOpen={showAppealModal}
        onClose={() => setShowAppealModal(false)}
      />
    </div>
  );
}

interface BlockedDomainCardProps {
  domain: BlockedDomain;
  onAppeal: (domain: BlockedDomain) => void;
}

function BlockedDomainCard({ domain, onAppeal }: BlockedDomainCardProps) {
  return (
    <Card hover>
      <CardContent>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h4 className="text-gray-900 text-sm sm:text-base break-words font-medium">
              {domain.domain}
            </h4>
            <p className="text-sm text-gray-600 break-words">
              Case: {domain.caseNumber} • Blocked: {domain.dateBlocked}
            </p>
          </div>
          <Badge variant="red" className="self-start">
            {domain.status}
          </Badge>
        </div>

        <div className="mb-3">
          <p className="text-sm text-gray-700">{domain.reason}</p>
        </div>

        <div className="space-y-2">
          <p className="text-sm text-gray-700">
            <strong>Decision rationale:</strong>
          </p>
          <p className="text-sm text-gray-600">{domain.rationale}</p>
        </div>

        {domain.legalViolations && domain.legalViolations.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-sm text-gray-700 mb-2">
              <strong>Legal violations cited:</strong>
            </p>
            <ul className="space-y-1">
              {domain.legalViolations.map((violation, vIndex) => (
                <li
                  key={vIndex}
                  className="text-sm text-gray-600 flex items-start gap-2"
                >
                  <span className="text-red-600 mt-0.5 flex-shrink-0">•</span>
                  <span>{violation}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {domain.appeals && domain.appeals.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-sm text-gray-700 mb-3">
              <strong>
                Appeal History ({domain.appeals.length} appeal
                {domain.appeals.length !== 1 ? "s" : ""}):
              </strong>
            </p>
            <div className="space-y-3">
              {domain.appeals.map((appeal, aIndex) => (
                <InfoBox key={aIndex} variant="gray">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <p className="text-sm text-gray-700">
                      <strong>Appeal #{aIndex + 1}</strong> - {appeal.date}
                    </p>
                    <Badge
                      variant={
                        appeal.decision === "Denied"
                          ? "red"
                          : appeal.decision === "Approved"
                            ? "green"
                            : "yellow"
                      }
                      size="sm"
                      className="self-start"
                    >
                      {appeal.decision}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">{appeal.explanation}</p>
                </InfoBox>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-gray-200">
          <Button
            variant="danger"
            size="sm"
            onClick={() => onAppeal(domain)}
            className="w-full sm:w-auto"
          >
            Appeal this decision
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
